import { FC } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Skeleton, Svg, Text, Tooltip } from '@hopskipdrive/react-components';
import { Edit as EditIcon, Map as MapIcon, SyncRounded as SyncRoundedIcon } from '@mui/icons-material';
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz';
import { includes } from 'lodash';
import pluralize from 'pluralize';

import { CopyToClipboard } from '../../../shared/components/CopyToClipboard';
import { LabelChip } from '../../../shared/components/LabelChip';
import { ButtonLink } from '../../../shared/components/Links';
import { ProfileImage } from '../../../shared/components/ProfileImage';
import { ProfileInitials } from '../../../shared/components/ProfileInitials';
import { colorTokens } from '../../../shared/components/variables';
import { getFullNameFirstLettersUpper } from '../../../shared/utils/helpers/getName';
import { estimatedTimeFromNowWithin24Hours } from '../../../shared/utils/helpers/util';
import { RIDE_STATES } from '../../constants/rideStates';
import { CancellationReason, EditRideDetails, RideDetails, RideOrganizer, RiderOnRide } from '../../types/rides';

import { ButtonsContainer, Heading, MapIconButton, RideInfo } from './PageHeading.styles';
import { RideRequiredServices } from './RideRequiredServices';
import { getStatusLabelProps } from './utils';

interface PageHeadingProps {
  rideDetails: RideDetails;
  isFetching: boolean;
  onMapButtonClick: () => void;
  isCarePartnerRide?: boolean;
}

export type OutletContextType = {
  rideSlug: string;
  timeZone: string;
  accountDetails: { accountName: string; accountSlug: string };
  ridersOnRide: RiderOnRide[];
  editRideDetails: EditRideDetails & { time: string };
  cancelReasons: CancellationReason[];
  organizers: RideOrganizer[];
  rideState: string;
};

export const PageHeading: FC<PageHeadingProps> = ({
  rideDetails,
  isFetching,
  onMapButtonClick,
  isCarePartnerRide = false,
}) => {
  return (
    <Heading $isCarePartnerRide={isCarePartnerRide}>
      <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
        <RideInfo>
          <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
            <MapIconButton disableRipple onClick={onMapButtonClick}>
              <MapIcon />
            </MapIconButton>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              {isFetching ? (
                <>
                  <Skeleton data-testid="skeleton" variant="text" width={180} />
                  <Skeleton variant="text" width={180} />
                </>
              ) : (
                <>
                  <Text variant="h5">{rideDetails?.timeAnchor === 'pick_up' ? 'Pickup Ride' : 'Drop-off Ride'}</Text>
                  <CopyToClipboard
                    toastMessage="Slug Copied"
                    text={rideDetails?.slug}
                    variant="subtitle1"
                    color={colorTokens.text.contrast}
                  />
                </>
              )}
            </Box>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-start" flexWrap="wrap">
            {isFetching ? (
              <>
                <Skeleton variant="text" width={362} height={31} />
                <Skeleton variant="text" width={209} height={17} />
              </>
            ) : (
              <>
                <StartDateTime
                  startDateTime={
                    rideDetails.mapDetails.orderedWaypointCoordinates[0].anchor
                      ? rideDetails.anchorTime
                      : rideDetails.mapDetails.orderedWaypointCoordinates[0].estimatedArrivalTime
                  }
                  timeZone={rideDetails.timeZone}
                />
                <Text
                  variant="caption"
                  style={{
                    fontWeight: 500,
                    color: 'rgba(255, 255, 255, 0.80)',
                  }}
                  data-testid="anchorTimeInLocalText"
                >
                  {formatInTimeZone(
                    new Date(rideDetails.anchorTime),
                    Intl.DateTimeFormat().resolvedOptions().timeZone,
                    "EEE, M/dd/yyyy 'at' h:mm aa zzz 'your time'"
                  )}
                </Text>
              </>
            )}
          </Box>
        </RideInfo>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap="12px" flexWrap="wrap">
        <Box display="flex" flexDirection="row" gap="6px" alignItems="center" flexWrap="wrap">
          <LabelChip data-testid="ride-state" isFetching={isFetching} {...getStatusLabelProps(rideDetails?.state)} />
          <LabelChip
            data-testid="ride-region"
            isFetching={isFetching}
            icon={<Svg icon="address" style={{ color: colorTokens.text.contrast }} />}
            label={rideDetails?.localeDetails.regionName}
            textColor={colorTokens.text.contrast}
            bgColor={'rgba(14, 134, 161, 0.50)'}
            border={'1px solid rgba(14, 134, 161, 0.50)'}
            onClick={() => window.open(rideDetails?.localeDetails.regionUrl, '_blank')}
          />
          <LabelChip
            data-testid="metro-area"
            isFetching={isFetching}
            icon={<MapIcon style={{ color: colorTokens.text.contrast }} />}
            label={rideDetails?.localeDetails.metroAreaName}
            textColor={colorTokens.text.contrast}
            bgColor={'rgba(14, 134, 161, 0.50)'}
            border={'1px solid rgba(14, 134, 161, 0.50)'}
            onClick={() => window.open(rideDetails?.localeDetails.metroAreaUrl, '_blank')}
          />
          <LabelChip
            data-testid="ride-account-type"
            isFetching={isFetching}
            icon={undefined}
            label={rideDetails?.accountDetails?.accountType}
            {...(rideDetails?.accountDetails?.accountType
              ? { bgColor: colorTokens.background.default, textColor: colorTokens.text.primary }
              : { bgColor: colorTokens.primary.light, textColor: colorTokens.text.contrast })}
          />
          {isCarePartnerRide && rideDetails?.requiredServices?.length && (
            <RideRequiredServices
              requiredServices={rideDetails.requiredServices.filter((service) => !!service.isCarePartnerProvided)}
              isFetching={isFetching}
            />
          )}
          <RidersList isFetching={isFetching} riders={rideDetails?.riders} />
        </Box>
        <ButtonsContainer>
          <Buttons
            isFetching={isFetching}
            rideState={rideDetails?.state}
            rideSlug={rideDetails?.slug}
            isCarePartnerRide={isCarePartnerRide}
          />
        </ButtonsContainer>
      </Box>
      {rideDetails && (
        <Outlet
          context={{
            rideState: rideDetails?.state,
            rideSlug: rideDetails?.slug,
            ridersOnRide: rideDetails?.riders,
            accountDetails: rideDetails?.accountDetails,
            editRideDetails: {
              ...rideDetails.editRideDetails,
              anchorTime: utcToZonedTime(rideDetails.editRideDetails.anchorTime, rideDetails?.timeZone),
              time: formatInTimeZone(new Date(rideDetails.editRideDetails.anchorTime), rideDetails?.timeZone, 'HH:mm'),
            },
            timeZone: rideDetails?.timeZone,
            cancelReasons: rideDetails?.rideCancellationReasons,
            organizers: rideDetails?.organizers,
          }}
        />
      )}
    </Heading>
  );
};

const RidersList = ({ isFetching, riders }) => {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" minWidth="333px">
      {isFetching ? (
        <>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={120} />
        </>
      ) : (
        <>
          {riders?.map((rider) =>
            rider.profilePictureUrl ? (
              <ProfileImage
                key={rider.id}
                imgSrc={rider.profilePictureUrl || ''}
                style={{ border: `1px solid ${colorTokens.primary.contrast}` }}
              />
            ) : (
              <ProfileInitials
                key={rider.id}
                fullName={getFullNameFirstLettersUpper(`${rider.firstName} ${rider.lastName}`)}
              />
            )
          )}
          <Text data-testid="rider-names" variant="caption" ml="8px">
            {`${pluralize('Rider', riders.length, true)}: `}
            {riders?.map((rider) => getFullNameFirstLettersUpper(`${rider.firstName} ${rider.lastName}`)).join(', ')}
          </Text>
        </>
      )}
    </Box>
  );
};

const Buttons = ({ isFetching, rideState, rideSlug, isCarePartnerRide }) => {
  const canEdit = !includes([...RIDE_STATES.completed, ...RIDE_STATES.inactive, ...RIDE_STATES.canceled], rideState);
  const canCancel = !includes([...RIDE_STATES.completed, ...RIDE_STATES.canceled], rideState);

  return isFetching ? (
    <>
      <Skeleton variant="rectangular" width={150} height={32} />
      <Skeleton variant="rectangular" width={150} height={32} />
    </>
  ) : (
    <>
      {canCancel && (
        <Tooltip title="Available in Admin" placement="bottom" disableHoverListener={!isCarePartnerRide}>
          <div>
            <ButtonLink
              variant="contained"
              color="warning"
              size="medium"
              to={`/rides/${rideSlug}/cancel`}
              disabled={isCarePartnerRide}
            >
              Cancel Ride
            </ButtonLink>
          </div>
        </Tooltip>
      )}
      {canEdit && (
        <Tooltip title="Available in Admin" placement="bottom" disableHoverListener={!isCarePartnerRide}>
          <div>
            <ButtonLink
              variant="contained"
              color="tonal variant"
              size="medium"
              to={`/rides/${rideSlug}/edit`}
              disabled={isCarePartnerRide}
            >
              <EditIcon /> Edit Ride
            </ButtonLink>
          </div>
        </Tooltip>
      )}
      {includes(RIDE_STATES.canceled, rideState) && (
        <Tooltip
          title="Currently unavailable on CarePartner rides"
          placement="bottom"
          disableHoverListener={!isCarePartnerRide}
        >
          <div>
            <ButtonLink
              variant="contained"
              color="tonal variant"
              size="medium"
              to={`/rides/${rideSlug}/rebuild`}
              disabled={isCarePartnerRide}
            >
              <SyncRoundedIcon /> Rebuild Ride
            </ButtonLink>
          </div>
        </Tooltip>
      )}
    </>
  );
};

const StartDateTime = ({ startDateTime, timeZone }) => {
  const startsAt = new Date(startDateTime);

  return (
    <Text variant="h5" data-testid="anchorTimeText">
      {formatInTimeZone(startsAt, timeZone, "EEE, M/dd/yyyy 'at' h:mm aa zzz")}
      {estimatedTimeFromNowWithin24Hours(startDateTime)}
    </Text>
  );
};

export const HeadingSkeleton = () => (
  <Heading>
    <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
      <RideInfo>
        <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
          <MapIconButton disableRipple disabled>
            <MapIcon />
          </MapIconButton>
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Skeleton data-testid="skeleton" variant="text" width={180} />
            <Skeleton variant="text" width={180} />
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-start" flexWrap="wrap">
          <Skeleton variant="text" width={362} height={31} />
          <Skeleton variant="text" width={209} height={17} />
        </Box>
      </RideInfo>
    </Box>
    <Box display="flex" alignItems="center" justifyContent="space-between" gap="12px" flexWrap="wrap">
      <Box display="flex" flexDirection="row" gap="6px" alignItems="center" flexWrap="wrap">
        <Skeleton variant="rectangular" width={120} height={35} />
        <Skeleton variant="rectangular" width={120} height={35} />
        <Skeleton variant="rectangular" width={120} height={35} />
        <Skeleton variant="rectangular" width={120} height={35} />
        <RidersList isFetching={true} riders={[]} />
      </Box>
      <ButtonsContainer>
        <Skeleton variant="rectangular" width={257} height={38} />
        <Skeleton variant="rectangular" width={257} height={38} />
      </ButtonsContainer>
    </Box>
  </Heading>
);
